import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocalStorage, setLocalStorage } from "@/core/utils/helpers";
import CONSTANTS from "@/core/utils/constants";
import CONFIGS from "@/core/utils/configs";
import { app, authentication } from "@microsoft/teams-js";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { reportTelemetryFailure } from "@/core/telemetry";

export const useInitialize = () => {
  const [{ inTeams }] = useTeamsContext();
  const { i18n } = useTranslation();
  const [theme, setTheme] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [tokenReady, setTokenReady] = useState(false);
  const [requiresBrowserAuth, setRequiresBrowserAuth] = useState(false);

  const requestBrowserAuth = async () => {
    if (inTeams) {
      // Must use popup inside Teams — notifySuccess/notifyFailure in /auth-end
      // only work when the page was opened via authentication.authenticate().
      try {
        const token = await authentication.authenticate({
          url: `${window.location.origin}/auth-start`,
          width: 600,
          height: 535,
          isExternal: true,
        });
        setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, token);
        setRequiresBrowserAuth(false);
        setTokenReady(true);
      } catch (err) {
        console.warn("Browser auth popup failed", err);
      }
    } else {
      window.location.href = `${window.location.origin}/auth-start`;
    }
  };

  useEffect(() => {
    if (inTeams === false) {
      if (CONFIGS.TEST_TOKEN) {
        setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, CONFIGS.TEST_TOKEN);
      }

      const browserToken =
        CONFIGS.TEST_TOKEN ||
        getLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN) ||
        "";

      setTokenReady(Boolean(browserToken));
      setRequiresBrowserAuth(!browserToken);
      setLoading(false);
      return;
    }

    const initializeApp = async () => {
      try {
        await app.initialize();
        const context = await app.getContext();

        setTheme(context.app.theme);
        app.registerOnThemeChangeHandler(setTheme);
        app.notifyAppLoaded();

        try {
          const token = await authentication.getAuthToken();
          setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, token);
          setTokenReady(true);
          app.notifySuccess();
        } catch (authError) {
          console.warn("GetAuthToken failed, attempting popup", authError);
          try {
            const popupToken = await authentication.authenticate({
              url: `${window.location.origin}/auth-start`,
              width: 600,
              height: 535,
              isExternal: true,
            });
            setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, popupToken);
            setTokenReady(true);
          } catch (popupError) {
            console.warn("Popup auth failed", popupError);
            reportTelemetryFailure("browser_auth_required", {
              reason: String(popupError),
              ssoReason: String(authError),
            });
            setRequiresBrowserAuth(true);
          }
        }
      } catch (initError) {
        // Expected when running outside Teams context
        if (inTeams) {
          reportTelemetryFailure("sdk_init", { reason: String(initError) });
        }
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [inTeams]);

  return {
    inTeams,
    i18n,
    theme,
    loading,
    tokenReady,
    requiresBrowserAuth,
    requestBrowserAuth,
  };
};
